import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultIlitOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'ilit-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is an ILIT?',
                variant: 'h3',
              },
            },
            {
              id: 'ilit-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "An Irrevocable Life Insurance Trust (ILIT) is a trust that is created to own a life insurance policy, and is often used as a way of moving the life insurance policy outside of the insured person's estate for estate tax purposes. The ILIT will own the life insurance policy and be the beneficiary of the policy's death benefit. **ILITs can help reduce estate taxes, provide liquidity to pay estate expenses, and ensure that beneficiaries receive the intended financial support.** Once an ILIT is set up, the grantor cannot change or terminate it.",
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'ilit-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'ilit-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'ilit-benefit-estate',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        "By removing the life insurance policy from the estate, the death benefit isn't included in the taxable estate, which can significantly reduce future estate tax liability.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'ilit-benefit-creditor',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Creditor protection',
                      description:
                        'Life insurance held in an ILIT can be shielded from creditors, ensuring that beneficiaries receive the intended benefits.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'ilit-benefit-control',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Control over policy and proceeds',
                      description:
                        'The grantor can set specific terms within the ILIT on how the policy proceeds should be distributed, thus retaining control over the use of the death benefit and ensuring long-term financial support of beneficiaries.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'ilit-benefit-liquidity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Enhanced liquidity',
                      description:
                        'An ILIT provides tax-free life insurance proceeds that can be used to cover estate taxes, debts, and other expenses, preventing the forced sale of estate assets. If the grantor holds mostly illiquid assets, an ILIT can be a good planning tool.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'ilit-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'ilit-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once the ILIT is established, the grantor cannot make changes or dissolve the trust. This lack of flexibility can be limiting if circumstances change.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'ilit-drawback-loss-of-access',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Loss of access',
                      description:
                        'The grantor cannot access or benefit from the life insurance policy once it is transferred into the ILIT, which can be a significant drawback if personal financial circumstances change.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'ilit-drawback-lookback',
                    type: 'PresentationListItem',
                    props: {
                      title: '3-year lookback rule',
                      description:
                        'If the grantor dies within three years of transferring an existing life insurance policy to the ILIT, the policy’s death benefit is included in the grantor’s taxable estate.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'ilit-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining an ILIT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
